import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../api/api";
import "./TurtleNotifications.css";

const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;
const NOTIFICATIONS_API = `${BASE_URL}/api/notifications/`;

// Как часто обновлять список — раз в минуту
const POLL_INTERVAL = 60_000;

const ICONS = {
  payment_pending: "💳",
  allocation_pending: "🧾",
  our_allocation_pending: "🏭",
  payment_unallocated: "📥",
  allocation_unallocated: "🔀",
};

const TurtleNotifications = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await authFetch(`${NOTIFICATIONS_API}?unread=1`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // DRF-пагинация: {count, next, previous, results: [...]}
      const list = Array.isArray(data) ? data : data.results || [];

      setNotifications(list);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      setError("Не удалось загрузить уведомления");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Первичная загрузка + опрос каждую минуту
  useEffect(() => {
    loadNotifications();

    const id = setInterval(loadNotifications, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [loadNotifications]);

  // Закрытие по клику вне и по Esc
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen((value) => !value);
    if (!open) loadNotifications();
  };

  const markRead = async (id) => {
    try {
      await authFetch(`${NOTIFICATIONS_API}${id}/read/`, {
        method: "POST",
      });
    } catch (err) {
      console.error("markRead failed:", err);
    }
    setNotifications((current) => current.filter((n) => n.id !== id));
  };

  const markAllRead = async () => {
    try {
      await authFetch(`${NOTIFICATIONS_API}read-all/`, {
        method: "POST",
      });
    } catch (err) {
      console.error("markAllRead failed:", err);
    }
    setNotifications([]);
  };

  const handleItemClick = async (item) => {
    await markRead(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  const formatTime = (iso) => {
    if (!iso) return "";

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";

    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60_000);

    if (diffMin < 1) return "только что";
    if (diffMin < 60) return `${diffMin} мин назад`;

    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} ч назад`;

    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const count = notifications.length;

  return (
    <div className="turtle" ref={wrapperRef}>
      <button
        type="button"
        className="turtle__button"
        onClick={handleOpen}
        aria-label="Уведомления"
        aria-expanded={open}
      >
        <TurtleIcon className="turtle__icon" />

        {count > 0 && (
          <span className="turtle__badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="turtle__dropdown" role="dialog">
          <div className="turtle__header">
            <span className="turtle__header-title">
              Уведомления {count > 0 && `(${count})`}
            </span>

            {count > 0 && (
              <button
                type="button"
                className="turtle__mark-read"
                onClick={markAllRead}
              >
                Прочитать все
              </button>
            )}
          </div>

          <div className="turtle__list">
            {error && <div className="turtle__error">{error}</div>}

            {!error && count === 0 && (
              <div className="turtle__empty">
                {loading
                  ? "Загрузка…"
                  : "Всё в порядке, зависших платежей нет"}
              </div>
            )}

            {notifications.map((item) => (
              <button
                key={item.id}
                type="button"
                className="turtle__item"
                onClick={() => handleItemClick(item)}
              >
                <div className="turtle__item-row">
                  <span className="turtle__item-emoji">
                    {ICONS[item.type] || "🔔"}
                  </span>
                  <span className="turtle__item-title">{item.title}</span>
                </div>

                <div className="turtle__item-text">{item.text}</div>

                <div className="turtle__item-date">
                  {formatTime(item.created_at)}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function TurtleIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 13a8 8 0 0 1 16 0z" />
      <circle cx="20" cy="13" r="1.6" />
      <path d="M21.2 12.4l1.4-.9" />
      <path d="M4 13l-1.6 1.1" />
      <path d="M7 13v1.6" />
      <path d="M12 13v1.6" />
      <path d="M17 13v1.6" />
      <path d="M4 13h16" />
    </svg>
  );
}

export default TurtleNotifications;